import React, { useEffect, useState } from "react";
import client from "../../lib/api";
import { FileText, Eye, Inbox, Lightbulb, Radio, PenSquare, Clock, PlayCircle, Copy, PlusCircle, Star, Search as SearchIcon, Flame, TrendingUp, Image, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CARDS = [
  { key: "published", label: "Published", icon: FileText, color: "bg-r24-red" },
  { key: "total_views", label: "Total Views", icon: Eye, color: "bg-black" },
  { key: "breaking", label: "Breaking Live", icon: Radio, color: "bg-r24-red" },
  { key: "review_queue", label: "In Review", icon: Inbox, color: "bg-black" },
  { key: "scheduled", label: "Scheduled", icon: Clock, color: "bg-r24-red" },
  { key: "drafts", label: "Drafts", icon: PenSquare, color: "bg-black" },
  { key: "videos", label: "Video News", icon: PlayCircle, color: "bg-r24-red" },
  { key: "duplicates", label: "Duplicate Warnings", icon: Copy, color: "bg-black" },
  { key: "news_tips", label: "Pending Tips", icon: Lightbulb, color: "bg-r24-red" },
];

// Quick-access control tiles mapped to Articles views / dedicated pages.
const TILES = [
  { to: "/admin/articles/new", label: "Create News", icon: PlusCircle },
  { to: "/admin/articles", label: "Edit / Update News", icon: FileText },
  { to: "/admin/articles?status=draft", label: "Drafts", icon: PenSquare },
  { to: "/admin/articles?status=scheduled", label: "Scheduled News", icon: Clock },
  { to: "/admin/articles?flag=breaking", label: "Breaking News", icon: Radio },
  { to: "/admin/articles?flag=featured", label: "Featured News", icon: Star },
  { to: "/admin/articles?flag=investigation", label: "Investigation Desk", icon: SearchIcon },
  { to: "/admin/articles?flag=trending", label: "Trending", icon: Flame },
  { to: "/admin/articles?sort=views", label: "Most Read", icon: Eye },
  { to: "/admin/articles?sort=views&flag=trending", label: "Fastest Growing", icon: TrendingUp },
  { to: "/admin/articles?flag=video", label: "Video News", icon: PlayCircle },
  { to: "/admin/media", label: "Media Library", icon: Image },
  { to: "/admin/articles?status=duplicate", label: "Duplicate Warnings", icon: Copy },
  { to: "/admin/profile", label: "SEO / Security Settings", icon: Settings },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [deduping, setDeduping] = useState(false);
  useEffect(() => { client.get("/admin/stats").then((r) => setStats(r.data)); }, []);

  const runDedup = async () => {
    setDeduping(true);
    try {
      const r = await client.post("/admin/dedup");
      toast.success(`Duplicate cleanup done — ${r.data.marked_duplicates} marked as duplicate.`);
      client.get("/admin/stats").then((rr) => setStats(rr.data));
    } catch (err) { toast.error("Dedup failed: " + (err.response?.data?.detail || err.message)); }
    setDeduping(false);
  };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-head font-bold text-3xl uc mb-1">Newsroom Dashboard</h1>
      <p className="text-gray-500 mb-8">Rapid24News editorial control center</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="bg-white border border-gray-200 p-5">
              <div className={`${c.color} text-white w-10 h-10 flex items-center justify-center mb-3`}><Icon size={20} /></div>
              <div className="font-head font-bold text-3xl">{stats ? (stats[c.key] ?? 0).toLocaleString() : "—"}</div>
              <div className="text-gray-500 text-sm uc font-semibold mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      <h2 className="font-head font-bold text-xl uc mb-4 border-b-2 border-r24-red pb-2">Newsroom Controls</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.label} to={t.to} data-testid={`tile-${t.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="group bg-white border border-gray-200 hover:border-r24-red p-4 flex items-center gap-3 transition-colors">
              <span className="bg-gray-100 group-hover:bg-r24-red group-hover:text-white text-gray-700 w-9 h-9 flex items-center justify-center shrink-0 transition-colors"><Icon size={18} /></span>
              <span className="font-head font-semibold text-sm leading-tight">{t.label}</span>
            </Link>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to="/admin/articles/new" className="bg-r24-red text-white font-bold uc text-sm px-6 py-3 hover:bg-r24-reddark" data-testid="dashboard-new-article">+ New Article</Link>
        <Link to="/admin/review" className="bg-black text-white font-bold uc text-sm px-6 py-3" data-testid="dashboard-review">Review Imported News</Link>
        <Link to="/admin/feeds" className="border-2 border-black font-bold uc text-sm px-6 py-3 hover:bg-black hover:text-white" data-testid="dashboard-feeds">Run Auto-Ingestion</Link>
        <button onClick={runDedup} disabled={deduping} className="border-2 border-black font-bold uc text-sm px-6 py-3 hover:bg-black hover:text-white disabled:opacity-50" data-testid="dashboard-dedup">{deduping ? "Cleaning…" : "Run Duplicate Cleanup"}</button>
      </div>
    </div>
  );
}
